import React from 'react';
import { API, C } from '../constants';
import LazyImg from './LazyImg';
import { groupEpisodes, isFinished, isInProgress } from '../utils';

// Películas a medias + una entrada por serie: el episodio a medias más reciente,
// o el siguiente sin terminar si el último que se vio ya se terminó.
function continueItems(library, history) {
  const bySeries = groupEpisodes(library);
  const items = library
    .filter(m => !bySeries[m.series_id]?.includes(m) && isInProgress(history[m.file]))
    .map(m => ({ media: m, isNext: false, updated: history[m.file].updated || 0 }));
  Object.values(bySeries).forEach(eps => {
    const watched = eps.filter(e => history[e.file]).sort((a, b) => (history[b.file].updated || 0) - (history[a.file].updated || 0));
    for (const ep of watched) {
      const h = history[ep.file];
      if (isInProgress(h)) { items.push({ media: ep, isNext: false, updated: h.updated || 0 }); return; }
      if (isFinished(h)) {
        const next = eps.slice(eps.indexOf(ep) + 1).find(e => !isFinished(history[e.file]));
        if (next) items.push({ media: next, isNext: !isInProgress(history[next.file]), updated: h.updated || 0 });
        return;
      }
    }
  });
  return items.sort((a, b) => b.updated - a.updated);
}

export default function ContinueRow({ library, history, mediaInfo, isMobile, isTV, onPlay }) {
  let rowEl = null;
  const items = continueItems(library, history);
  if (items.length === 0) return null;
  const cw  = isMobile ? 118 : isTV ? 214 : 158;
  const ch  = Math.round(cw * 1.5);
  const gap = isMobile ? 10 : 16;
  const scroll   = dir => { if (rowEl) rowEl.scrollBy({ left: dir * (cw + gap) * 3, behavior: 'smooth' }); };
  const fmtRem   = secs => { if (!secs || secs <= 0) return ''; const m = Math.floor(secs / 60); if (m < 1) return '<1m'; const h = Math.floor(m / 60); return h > 0 ? `${h}h ${m % 60}m` : `${m}m`; };
  const btnStyle = { width: isTV?'46px':'36px', height: isTV?'46px':'36px', background:'rgba(255,255,255,0.07)', border:`1px solid ${C.accentBorder}`, color:C.textMuted, borderRadius:'50%', cursor:'pointer', fontSize: isTV?'24px':'20px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.2s', lineHeight:1, fontFamily:'Inter, sans-serif' };
  return (
    <div style={{ marginBottom: isMobile?'36px':'56px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom: isMobile?'12px':'18px' }}>
        <div style={{ width:'3px', height:'20px', background:`linear-gradient(180deg,${C.accentSoft},${C.accent})`, borderRadius:'2px', flexShrink:0 }} />
        <h2 style={{ fontFamily:'Playfair Display, serif', fontSize: isMobile?'16px':isTV?'22px':'18px', fontWeight:'600', color:C.text }}>▶ Continuar viendo</h2>
        <span style={{ color:C.textDim, fontSize:'12px' }}>{items.length} título{items.length!==1?'s':''}</span>
        {items.length > 3 && (
          <div style={{ marginLeft:'auto', display:'flex', gap:'8px' }}>
            <button onClick={() => scroll(-1)} style={btnStyle}
              onMouseEnter={e=>{e.currentTarget.style.background=C.accentDim;e.currentTarget.style.color=C.accentSoft;e.currentTarget.style.borderColor=C.accent;}}
              onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.07)';e.currentTarget.style.color=C.textMuted;e.currentTarget.style.borderColor=C.accentBorder;}}>‹</button>
            <button onClick={() => scroll(1)} style={btnStyle}
              onMouseEnter={e=>{e.currentTarget.style.background=C.accentDim;e.currentTarget.style.color=C.accentSoft;e.currentTarget.style.borderColor=C.accent;}}
              onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.07)';e.currentTarget.style.color=C.textMuted;e.currentTarget.style.borderColor=C.accentBorder;}}>›</button>
          </div>
        )}
      </div>
      <div ref={el => { rowEl = el; }} style={{ display:'flex', gap:`${gap}px`, overflowX:'auto', WebkitOverflowScrolling:'touch', paddingBottom:'4px', scrollSnapType:'x proximity' }}>
        {items.map(({ media, isNext }) => {
          const info      = mediaInfo[media.file];
          const savedH    = history[media.file];
          const title     = info?.title || media.name;
          const pct       = isNext ? 0 : Math.min((savedH.progress / savedH.duration) * 100, 99);
          const remaining = isNext ? '' : fmtRem(savedH.duration - savedH.progress);
          return (
            <div key={media.id}
              onClick={() => { onPlay(media); window.scrollTo({ top:0, behavior:'smooth' }); }}
              style={{ flexShrink:0, width:`${cw}px`, background:C.bgCard, borderRadius:'12px', overflow:'hidden', cursor:'pointer', border:`1px solid ${C.border}`, transition:'transform 0.25s, box-shadow 0.25s, border-color 0.25s', scrollSnapAlign:'start', position:'relative' }}
              onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.06) translateY(-4px)';e.currentTarget.style.boxShadow='0 20px 48px rgba(0,0,0,0.75)';e.currentTarget.style.borderColor=C.accentBorder;}}
              onMouseLeave={e=>{e.currentTarget.style.transform='scale(1)';e.currentTarget.style.boxShadow='none';e.currentTarget.style.borderColor=C.border;}}>
              {media.thumbnail
                ? <LazyImg src={`${API}/thumbnail/${encodeURIComponent(media.thumbnail)}`} alt={title} style={{ width:'100%', height:`${ch}px`, objectFit:'cover', objectPosition:'center top', display:'block' }} />
                : <div style={{ width:'100%', height:`${ch}px`, background:'linear-gradient(135deg,#1c1008,#120a04)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'36px' }}>{media.type==='movie'?'🎬':'📺'}</div>
              }
              <div style={{ position:'absolute', bottom:0, left:0, right:0, height:`${Math.round(ch * 0.6)}px`, background:'linear-gradient(to top,rgba(15,10,8,0.98) 0%,rgba(15,10,8,0.55) 55%,transparent 100%)', pointerEvents:'none' }} />
              <div style={{ position:'absolute', top:'36%', left:'50%', transform:'translate(-50%,-50%)', pointerEvents:'none' }}>
                <div style={{ width: isTV?'52px':'42px', height: isTV?'52px':'42px', borderRadius:'50%', background:`linear-gradient(135deg,${C.accent},${C.accentSoft})`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 6px 20px rgba(201,113,74,0.5)` }}>
                  <span style={{ color:'#fff', fontSize: isTV?'20px':'15px', marginLeft:'3px' }}>▶</span>
                </div>
              </div>
              <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'8px 10px 8px' }}>
                <p style={{ color:C.text, fontSize: isTV?'13px':'11px', fontWeight:'600', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:'1.3', marginBottom:'6px' }}>{title}</p>
                <div style={{ height:'3px', background:'rgba(255,255,255,0.15)', borderRadius:'2px', marginBottom:'4px' }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:`linear-gradient(90deg,${C.accent},${C.accentSoft})`, borderRadius:'2px' }} />
                </div>
                <p style={{ color:C.textMuted, fontSize:'10px' }}>{isNext ? 'Siguiente episodio' : `${remaining} restantes`}</p>
              </div>
            </div>
          );
        })}
        <div style={{ flexShrink:0, width:`${gap}px` }} />
      </div>
    </div>
  );
}
